import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { adminApi } from '../../services/api';
import {
  ArrowLeft,
  MessageSquare,
  Clock,
  User,
  Building2,
  Tag,
  Send,
  Paperclip,
  MoreVertical,
  CheckCircle,
  AlertCircle,
  XCircle
} from 'lucide-react';
import toast from 'react-hot-toast';

const statusColors = {
  open: 'badge-warning',
  in_progress: 'badge-info',
  waiting: 'badge-gray',
  resolved: 'badge-success',
  closed: 'badge-gray'
};

const priorityColors = {
  low: 'bg-gray-100 text-gray-700',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-orange-100 text-orange-700',
  urgent: 'bg-red-100 text-red-700'
};

const TicketDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyContent, setReplyContent] = useState('');
  const [sending, setSending] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);

  useEffect(() => {
    fetchTicket();
    fetchMessages();
  }, [id]);

  const fetchTicket = async () => {
    try {
      const response = await adminApi.getSupportTicket(id);
      setTicket(response.data.data);
    } catch (err) {
      toast.error('Failed to load ticket');
      navigate('/support');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await adminApi.getTicketMessages(id);
      setMessages(response.data.data || []);
    } catch (err) {
      console.error('Failed to load messages:', err);
    }
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!replyContent.trim()) return;

    setSending(true);
    try {
      await adminApi.replyToTicket(id, { content: replyContent });
      toast.success('Reply sent');
      setReplyContent('');
      fetchMessages();
    } catch (err) {
      toast.error('Failed to send reply');
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    try {
      await adminApi.updateTicketStatus(id, newStatus);
      toast.success('Status updated');
      setShowStatusModal(false);
      fetchTicket();
    } catch (err) {
      toast.error('Failed to update status');
      console.error(err);
    }
  };

  const handleUpdatePriority = async (newPriority) => {
    try {
      await adminApi.updateTicketPriority(id, newPriority);
      toast.success('Priority updated');
      fetchTicket();
    } catch (err) {
      toast.error('Failed to update priority');
      console.error(err);
    }
  };

  const handleAssign = async (assigneeId) => {
    try {
      await adminApi.assignTicket(id, assigneeId);
      toast.success('Ticket assigned');
      fetchTicket();
    } catch (err) {
      toast.error('Failed to assign ticket');
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!ticket) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <Link
            to="/support"
            className="mr-4 p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900">
                #{ticket.ticketNumber || ticket.id.slice(0, 8)}
              </h1>
              <span className={statusColors[ticket.status] || 'badge-gray'}>
                {ticket.status?.replace('_', ' ')}
              </span>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                priorityColors[ticket.priority] || priorityColors.medium
              }`}>
                {ticket.priority}
              </span>
            </div>
            <p className="text-gray-500 mt-1">{ticket.subject}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button onClick={() => setShowStatusModal(true)} className="btn-secondary">
            Update Status
          </button>
          {ticket.status !== 'resolved' && ticket.status !== 'closed' && (
            <button
              onClick={() => handleUpdateStatus('resolved')}
              className="btn-primary"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Mark Resolved
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Original Ticket */}
          <div className="card p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center mr-3">
                  <User className="w-5 h-5 text-primary-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">{ticket.requesterName}</p>
                  <p className="text-xs text-gray-500">{ticket.requesterEmail}</p>
                </div>
              </div>
              <div className="flex items-center text-xs text-gray-500">
                <Clock className="w-3 h-3 mr-1" />
                {new Date(ticket.createdAt).toLocaleString()}
              </div>
            </div>
            <div className="prose prose-sm max-w-none text-gray-700">
              {ticket.description}
            </div>
            {ticket.attachments && ticket.attachments.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-xs text-gray-500 mb-2">Attachments</p>
                <div className="flex flex-wrap gap-2">
                  {ticket.attachments.map((attachment, index) => (
                    <a
                      key={index}
                      href={attachment.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center px-3 py-2 bg-gray-50 rounded-lg hover:bg-gray-100 text-sm text-gray-600"
                    >
                      <Paperclip className="w-4 h-4 mr-2" />
                      {attachment.name}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Messages */}
          <div className="card">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Conversation ({messages.length})
              </h2>
            </div>
            <div className="divide-y divide-gray-100">
              {messages.length === 0 ? (
                <div className="px-6 py-8 text-center text-gray-500">
                  No replies yet
                </div>
              ) : (
                messages.map((message) => (
                  <div key={message.id} className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${
                          message.isAdmin ? 'bg-primary-100' : 'bg-gray-100'
                        }`}>
                          <User className={`w-4 h-4 ${
                            message.isAdmin ? 'text-primary-600' : 'text-gray-600'
                          }`} />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 text-sm">
                            {message.senderName}
                            {message.isAdmin && (
                              <span className="ml-2 px-1.5 py-0.5 bg-primary-100 text-primary-700 rounded text-xs">
                                Support
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500">
                        {new Date(message.createdAt).toLocaleString()}
                      </p>
                    </div>
                    <div className="prose prose-sm max-w-none text-gray-700 ml-11">
                      {message.content}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Reply Form */}
          {ticket.status !== 'closed' && (
            <div className="card p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Reply</h3>
              <form onSubmit={handleSendReply}>
                <textarea
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                  className="input"
                  rows={4}
                  placeholder="Type your reply..."
                  required
                />
                <div className="flex items-center justify-between mt-4">
                  <button type="button" className="btn-secondary">
                    <Paperclip className="w-4 h-4 mr-2" />
                    Attach File
                  </button>
                  <button
                    type="submit"
                    disabled={sending || !replyContent.trim()}
                    className="btn-primary disabled:opacity-50"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {sending ? 'Sending...' : 'Send Reply'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Ticket Info */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Ticket Details</h2>
            <dl className="space-y-4">
              <div>
                <dt className="text-sm text-gray-500">Requester</dt>
                <dd className="mt-1">
                  <div className="flex items-center text-sm">
                    <User className="w-4 h-4 mr-2 text-gray-400" />
                    <div>
                      <p className="font-medium text-gray-900">{ticket.requesterName}</p>
                      <p className="text-xs text-gray-500">{ticket.requesterEmail}</p>
                    </div>
                  </div>
                </dd>
              </div>

              <div>
                <dt className="text-sm text-gray-500">Tenant</dt>
                <dd className="mt-1">
                  <Link
                    to={`/tenants/${ticket.tenantId}`}
                    className="flex items-center text-sm text-primary-600 hover:text-primary-700"
                  >
                    <Building2 className="w-4 h-4 mr-2" />
                    {ticket.tenantName}
                  </Link>
                </dd>
              </div>

              <div>
                <dt className="text-sm text-gray-500">Priority</dt>
                <dd className="mt-1">
                  <select
                    value={ticket.priority}
                    onChange={(e) => handleUpdatePriority(e.target.value)}
                    className="input text-sm"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </dd>
              </div>

              <div>
                <dt className="text-sm text-gray-500">Assigned To</dt>
                <dd className="mt-1">
                  <select
                    value={ticket.assignedToId || ''}
                    onChange={(e) => handleAssign(e.target.value)}
                    className="input text-sm"
                  >
                    <option value="">Unassigned</option>
                    <option value="admin-1">Admin User 1</option>
                    <option value="admin-2">Admin User 2</option>
                    <option value="admin-3">Admin User 3</option>
                  </select>
                </dd>
              </div>

              <div>
                <dt className="text-sm text-gray-500">Created</dt>
                <dd className="mt-1 text-sm text-gray-900 flex items-center">
                  <Clock className="w-4 h-4 mr-2 text-gray-400" />
                  {new Date(ticket.createdAt).toLocaleString()}
                </dd>
              </div>

              {ticket.resolvedAt && (
                <div>
                  <dt className="text-sm text-gray-500">Resolved</dt>
                  <dd className="mt-1 text-sm text-gray-900 flex items-center">
                    <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                    {new Date(ticket.resolvedAt).toLocaleString()}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* Tags */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Tags</h2>
            <div className="flex flex-wrap gap-2">
              {ticket.tags && ticket.tags.length > 0 ? (
                ticket.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-sm"
                  >
                    {tag}
                  </span>
                ))
              ) : (
                <p className="text-sm text-gray-500">No tags</p>
              )}
            </div>
            <button className="btn-secondary w-full mt-4 text-sm">
              <Tag className="w-4 h-4 mr-2" />
              Add Tag
            </button>
          </div>

          {/* Quick Actions */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
            <div className="space-y-2">
              <button
                onClick={() => handleUpdateStatus('in_progress')}
                disabled={ticket.status === 'in_progress'}
                className="w-full btn-secondary justify-start text-sm disabled:opacity-50"
              >
                <Clock className="w-4 h-4 mr-2" />
                Mark In Progress
              </button>
              <button
                onClick={() => handleUpdateStatus('waiting')}
                disabled={ticket.status === 'waiting'}
                className="w-full btn-secondary justify-start text-sm disabled:opacity-50"
              >
                <AlertCircle className="w-4 h-4 mr-2" />
                Waiting for Customer
              </button>
              <button
                onClick={() => handleUpdateStatus('closed')}
                disabled={ticket.status === 'closed'}
                className="w-full btn-secondary justify-start text-sm text-red-600 hover:text-red-700 disabled:opacity-50"
              >
                <XCircle className="w-4 h-4 mr-2" />
                Close Ticket
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Status Update Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Update Status</h3>
            <div className="space-y-2">
              {['open', 'in_progress', 'waiting', 'resolved', 'closed'].map((status) => (
                <button
                  key={status}
                  onClick={() => handleUpdateStatus(status)}
                  className={`w-full p-3 rounded-lg border text-left transition-colors ${
                    ticket.status === status
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-primary-300'
                  }`}
                >
                  {status.replace('_', ' ').charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowStatusModal(false)}
              className="w-full btn-secondary mt-4"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketDetailPage;
